/**
 * Keyword DOM Manager
 * 
 * Handles all DOM manipulations for the keyword management interface.
 * Follows SRP - only manages DOM updates, no Stimulus-specific code.
 */

const ANIMATION_DURATION = {
  FADE_IN: 500,
  FADE_OUT: 3000
}

const CSS_CLASSES = {
  SUCCESS: 'success',
  ERROR: 'error',
  SHOW: 'show'
}

/**
 * Shows a message with fade in/out animation
 * @param {HTMLElement} messageElement - The element to display the message in
 * @param {string} message - The message text to display
 * @param {boolean} isSuccess - Whether this is a success or error message
 */
export function showMessage(messageElement, message, isSuccess) {
  if (!messageElement) return

  messageElement.classList.remove(CSS_CLASSES.SUCCESS, CSS_CLASSES.ERROR, CSS_CLASSES.SHOW)
  messageElement.classList.add(isSuccess ? CSS_CLASSES.SUCCESS : CSS_CLASSES.ERROR)
  messageElement.innerHTML = message
  messageElement.classList.add(CSS_CLASSES.SHOW)

  fadeInOut(messageElement)
}

/**
 * Performs fade in/out animation on an element
 * @param {HTMLElement} element - The element to animate
 */
function fadeInOut(element) {
  element.style.opacity = '0'
  
  // Fade in
  const fadeIn = element.animate(
    [{ opacity: 0 }, { opacity: 1 }],
    { duration: ANIMATION_DURATION.FADE_IN, fill: 'forwards' }
  )

  fadeIn.onfinish = () => {
    // Fade out after fade in completes
    const fadeOut = element.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: ANIMATION_DURATION.FADE_OUT, fill: 'forwards' }
    )

    fadeOut.onfinish = () => {
      element.innerHTML = ''
      element.classList.remove(CSS_CLASSES.SHOW, CSS_CLASSES.SUCCESS, CSS_CLASSES.ERROR)
      element.style.opacity = ''
    }
  }
}

/**
 * Shows the edit form for a specific keyword
 * @param {string} keywordIdentifier - The unique identifier for the keyword
 */
export function showEditForm(keywordIdentifier) {
  const displayElement = document.getElementById(`${keywordIdentifier}_display`)
  const editElement = document.getElementById(`${keywordIdentifier}_edit`)
  const chipElement = document.getElementById(`${keywordIdentifier}_item`)

  // Hide display, show edit
  if (displayElement) {
    displayElement.classList.add('d-none')
  }
  if (editElement) {
    editElement.classList.add('active')
  }
  if (chipElement) {
    chipElement.classList.add('editing')
  }

  // Focus the input
  const input = document.getElementById(`${keywordIdentifier}_input`)
  if (input) {
    input.focus()
    input.select()
  }
}

/**
 * Hides the edit form for a specific keyword and restores original value
 * @param {string} keywordIdentifier - The unique identifier for the keyword
 */
export function hideEditForm(keywordIdentifier) {
  const displayElement = document.getElementById(`${keywordIdentifier}_display`)
  const editElement = document.getElementById(`${keywordIdentifier}_edit`)
  const chipElement = document.getElementById(`${keywordIdentifier}_item`)
  const textElement = document.getElementById(`${keywordIdentifier}_keyword_text`)

  // Show display, hide edit
  if (displayElement) {
    displayElement.classList.remove('d-none')
  }
  if (editElement) {
    editElement.classList.remove('active')
  }
  if (chipElement) {
    chipElement.classList.remove('editing')
  }

  // Restore original text value to the input
  const input = document.getElementById(`${keywordIdentifier}_input`)
  if (input && textElement) {
    input.value = textElement.textContent.trim()
  }
}

/**
 * Updates the keyword text after a successful edit
 * @param {string} keywordIdentifier - The unique identifier for the keyword
 * @param {string} newText - The new keyword text
 */
export function updateKeywordText(keywordIdentifier, newText) {
  const textElement = document.getElementById(`${keywordIdentifier}_keyword_text`)
  if (textElement) {
    textElement.textContent = newText
  }
}

/**
 * Removes a keyword item from the list
 * @param {string} keywordItemId - The ID of the keyword item to remove
 */
export function removeKeywordItem(keywordItemId) {
  const item = document.getElementById(keywordItemId)
  if (item) {
    item.remove()
  }
}
